import { ChangeDetectorRef, Component, HostListener, OnInit } from "@angular/core";
import { ColDef, FilterChangedEvent, GridApi, GridOptions, RowNode, GridReadyEvent, SelectionChangedEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ColParams } from "src/app/shared/grid/ag-grid";
import { PlanRegion } from "src/app/generated-models/PlanRegion";
import { ModalService } from "src/app/shared/modal.service";
import { PlanSubmission } from "src/app/generated-models/PlanSubmission";
import * as CDs from "src/app/utility/commonColDef";
import { extendDefaultOptions, setupFilterByLocalData, cacheFilterStatsLocal } from "../../utility/gridHelper";
import { BaseComponent } from "../../shared/base.component";
import { LoadingScreenService } from "../../shared/loading-screen.service";
import { ApiService } from "../../services";
import { PlanKindEnum, RegionStatusEnum } from "src/app/shared/enums/plan-enums";
import { Region } from "src/app/generated-models";
import { IUserProfile, PlanListRecord, RegionStatus, allowedCommonRegionStatuses } from "src/app/npr-request.model";

interface RowDataForRegion {
  RegionName: string;
  RegionStatus: string;
  Blueprint: string;
  AssignedVersion?: number;
  AlreadyAssigned?: string;
  AssignedBy?: string;
  AssignedTime?: Date | null;
  ApprovedBy?: string;
  ApprovedTime?: Date | null;
  ValidTo?: string;
  ValidToDate?: Date | null;
  RegionalPlanStatus?: string;
  IsCappedForCapacityOrder: boolean;
}

@Component({
  standalone: false,
  templateUrl: "./plans-assignment-service-owner.component.html",
  styleUrls: ["../../styles.scss", "./plan.scss"],
})
export class PlansAssignmentServiceOwnerComponent extends BaseComponent implements OnInit {
  regions: Region[] = [];
  selectedRegion: Region;
  regionEnum = RegionStatusEnum;
  userProfile: IUserProfile = null;

  rowDataForRegion: RowDataForRegion[] = [];

  gridOptionsForRegion: GridOptions;
  gridApi: GridApi;
  initPageSize = 15;

  blueprints: PlanListRecord[] = [];
  selectedBlueprint: PlanListRecord;

  planRegions: PlanRegion[] = [];

  hideAlreadyAssigned = false;
  hasRowSelected = false;

  keyFilterStatusMultiRegions = "filter:one-to-multiple";
  maxRegionNumOneTime = 10;
  canAssign = false;
  assignTooManyRegions = false;

  constructor(
    private apiService: ApiService,
    private modalService: ModalService,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  async ngOnInit() {
    this.hasRowSelected = false;
    this.canAssign = false;

    const RegionsColumnDefinition: ColDef[] = [
      {
        colId: "customFilter",
        valueGetter: (params: ColParams<RowDataForRegion, void>) => {
          if (this.hideAlreadyAssigned) {
            return this.isAssignable(params.data);
          }

          return true;
        },
        filterParams: {
          values: ["true", "false"],
          newRowsAction: "keep",
        },
        hide: true,
      },
      {
        ...CDs.RegionColDef("RegionName"),
        checkboxSelection: true,
        headerCheckboxSelection: true,
      },
      CDs.Blueprint,
      CDs.LatestVersion,
      CDs.AssignedVersion,
      CDs.AssignedBy,
      CDs.AssignedTime,
      CDs.RegionalPlanStatus,
      CDs.ApprovedBy,
      CDs.ApprovedTime,
      CDs.ValidTo,
      CDs.IsCappedForCapacityOrder,
    ];

    this.gridOptionsForRegion = extendDefaultOptions({
      columnDefs: RegionsColumnDefinition,
      suppressPaginationPanel: true,
      isRowSelectable: (node: RowNode) => this.isAssignable(node.data),
      enableRangeSelection: false,
      rowSelection: "multiple",
      animateRows: true,
      statusBar: {
        statusPanels: [
          { statusPanel: "agTotalAndFilteredRowCountComponent" },
          { statusPanel: "agTotalRowCountComponent" },
          { statusPanel: "agFilteredRowCountComponent" },
          { statusPanel: "agSelectedRowCountComponent" },
          { statusPanel: "agAggregationComponent" },
        ],
      },
    });

    this.apiService.getUserProfile().then(async (response) => {
      this.userProfile = response;
      if (response) {
        // Region List
        // includeDisabled = true, includeAirGapped = false
        const regionListResponse = await this.apiService.getRegionList(false, false).toPromise();
        if (!regionListResponse) {
          console.warn("No region obtained from server.");
        } else {
          this.regions = regionListResponse
            .filter((region) => allowedCommonRegionStatuses.includes(region.Status))
            .sort((region1, region2) => {
              return this.regionEnum[region1.Status.replace(/\s/g, "")] - this.regionEnum[region2.Status.replace(/\s/g, "")];
            })
            .filter((r) => !r.RegionName.endsWith("SLV"));
        }
      }
    });

    this.loadingService.setLoading(true);

    // Plan List
    const planListResponse = await this.apiService.getMyPlans().toPromise();
    if (!planListResponse) {
      console.warn("No service blueprints returned from server.");
    } else {
      this.blueprints = planListResponse
        .filter((p) => !p.IsMigrated)
        .filter((p) => p.Version > 0)
        .map((p) => ({ ...p, Label: `${p.Name} - ${p.ServiceTeam} (${p.ServiceTreeId})` } as PlanListRecord));
    }

    this.loadingService.setLoading(false);
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setFilterModel({
      customFilter: { values: ["true"] },
    });
  }

  async getLatestRegionalPlans(serviceTreeId: string): Promise<void> {
    return this.apiService
      .getLatestRegionalPlans(serviceTreeId)
      .toPromise()
      .then(
        (response) => {
          this.planRegions = response;
          setupFilterByLocalData(this.gridApi, this.keyFilterStatusMultiRegions);
        },
        (e: any) => {
          this.planRegions = [];
          this.notificationService.error(e);
        }
      );
  }

  async onBlueprintChanged(): Promise<void> {
    this.rowDataForRegion = [];

    if (this.selectedBlueprint) {
      this.gridApi.showLoadingOverlay();
      await this.getLatestRegionalPlans(this.selectedBlueprint.ServiceTreeId);
      this.gridApi.hideOverlay();

      const rowData: RowDataForRegion[] = [];
      const blueprint = this.selectedBlueprint.Name;
      this.regions.forEach((region) => {
        const row: RowDataForRegion = {
          RegionName: region.RegionName,
          RegionStatus: region.Status,
          Blueprint: blueprint,
          IsCappedForCapacityOrder: region.IsCappedForCapacityOrder,
        };

        rowData.push(row);
      });

      rowData.forEach((row) => {
        row.AlreadyAssigned = "No";

        const planRegion = this.planRegions.find(
          (p) => p.Region === row.RegionName && p.ServiceTreeId === this.selectedBlueprint.ServiceTreeId && p.Blueprint === blueprint
        );
        if (planRegion) {
          if (planRegion.Version === this.selectedBlueprint.Version) {
            row.AlreadyAssigned = "Yes";
          }

          row.AssignedVersion = planRegion.Version;
          row.AssignedBy = planRegion.Submitter;
          row.AssignedTime = planRegion.SubmitTime;
          row.ApprovedBy = planRegion.ApprovedBy;
          row.ApprovedTime = planRegion.ApprovedTime;
          row.ValidTo = planRegion.ValidTo;
          row.ValidToDate = planRegion.ValidToDate;
          row.RegionalPlanStatus = planRegion.Status;
        }
      });
      this.rowDataForRegion = rowData;
      this.hasRowSelected = false;
      this.canAssign = false;
      setTimeout(() => {
        this.gridApi.autoSizeAllColumns();
        if (this.selectedBlueprint.Name) {
          this.gridApi.setColumnVisible(CDs.Blueprint.headerName, false);
        }
      }, 100);
    }
    setupFilterByLocalData(this.gridApi, this.keyFilterStatusMultiRegions);
  }

  // Plan is not able to be assigned if the latest version is already assigned to the region
  isAssignable(row: RowDataForRegion): boolean {
    return row.AlreadyAssigned === "No" || (this.selectedBlueprint != null && (this.selectedBlueprint.Kind == PlanKindEnum.Growth && row.ValidTo !== null));
  }

  onHideAlreadyAssignedChanged(): void {
    this.gridApi.onFilterChanged();
  }

  async showPlanAssignmentDialog(): Promise<void> {
    try {
      const result = await this.modalService.planAssignmentModal();
      await this.assignPlans(result.startDate);
    } catch {
      // For the model dialog dismiss
      return;
    }
  }

  async assignPlans(startDate: Date): Promise<void> {
    const selectedRows = this.gridApi.getSelectedRows() as RowDataForRegion[];
    const planSubmissions = selectedRows.map((r) => {
      return {
        ServiceTreeId: this.selectedBlueprint.ServiceTreeId,
        ServiceTeam: this.selectedBlueprint.ServiceTeam,
        Version: this.selectedBlueprint.Version,
        Blueprint: this.selectedBlueprint.Name,
        Region: r.RegionName,
        StartDate: startDate,
      } as PlanSubmission;
    });

    await this.modalService.planAssignmentResponseModal(planSubmissions);
    await this.getLatestRegionalPlans(this.selectedBlueprint.ServiceTreeId);

    // reload grid
    this.onBlueprintChanged();
  }

  onSelectionChanged(params: SelectionChangedEvent): void {
    var rows = params.api.getSelectedRows().length;
    this.hasRowSelected = rows > 0;
    this.assignTooManyRegions = rows > this.maxRegionNumOneTime;
    const groupedRows = new Map<string, Set<string>>();

    const selectedRows = this.gridApi.getSelectedRows() as RowDataForRegion[];
    selectedRows.forEach((row) => {
      if (row.RegionStatus != RegionStatus.GA) {
        return;
      }

      const key = row.RegionName;
      if (!groupedRows.has(key)) {
        groupedRows.set(key, new Set<string>());
      }
    });

    if (!this.userProfile.IsAdmin && this.assignTooManyRegions) {
      this.canAssign = false;
    } else {
      this.canAssign = true;
    }
    this.cdr.detectChanges();
  }

  @HostListener("window:resize")
  onResize(): void {
    setTimeout(() => {
      this.gridApi.autoSizeAllColumns();
    }, 100);
  }

  onFilterChangedPlan(event: FilterChangedEvent): void {
    cacheFilterStatsLocal(event, this.keyFilterStatusMultiRegions);
  }
}
